/** Fetch wrapper: bearer token in memory, transparent refresh via the httpOnly cookie on 401. */

let accessToken: string | null = null;
let onSession: ((user: SessionUser | null) => void) | null = null;

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  timezone: string;
}

export const getToken = () => accessToken;
export function setSession(token: string | null, user?: SessionUser | null) {
  accessToken = token;
  if (user !== undefined) onSession?.(user);
}
export function onSessionChange(cb: (user: SessionUser | null) => void) {
  onSession = cb;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: Record<string, unknown>) {
    super(message);
  }
}

/** 'denied' = the server refused the session (signed out); 'offline' = it couldn't be reached at all. */
export type RefreshResult = 'ok' | 'denied' | 'offline';
let refreshing: Promise<RefreshResult> | null = null;

export function refreshSession(): Promise<RefreshResult> {
  refreshing ??= (async (): Promise<RefreshResult> => {
    try {
      const r = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'x-vtf': '1' } });
      if (r.status >= 500) return 'offline';
      if (!r.ok) {
        setSession(null, null);
        return 'denied';
      }
      const b = (await r.json()) as { accessToken: string; user: SessionUser };
      setSession(b.accessToken, b.user);
      return 'ok';
    } catch {
      return 'offline';
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

export async function refresh(): Promise<boolean> {
  return (await refreshSession()) === 'ok';
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const doFetch = () =>
    fetch(`/api/v1${path}`, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      credentials: 'include',
      headers: { ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  let res = await doFetch();
  // the sign-in steps answer 401 for a wrong password or code; every other 401 means the access token expired
  if (res.status === 401 && !/^\/auth\/(login|2fa|refresh|logout)/.test(path) && (await refresh())) res = await doFetch();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const issues = Array.isArray(body.issues) ? (body.issues as { path: string; message: string }[]).map((i) => `${i.path}: ${i.message}`).join(' · ') : '';
    throw new ApiError(res.status, String(body.message ?? res.statusText) + (issues ? ` (${issues})` : ''), body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Something went wrong.');

/** Multipart upload (e.g. library documents) with the same bearer token and refresh-on-401 as api(). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const doFetch = () => fetch(`/api/v1${path}`, { method: 'POST', credentials: 'include', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}, body: form });
  let res = await doFetch();
  if (res.status === 401 && (await refresh())) res = await doFetch();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(res.status, String(body.message ?? (res.status === 413 ? 'That file is too large.' : res.statusText)), body);
  }
  return (await res.json()) as T;
}
